import { Component, ErrorInfo, ReactNode } from "react";
import { isChunkLoadError, attemptChunkReloadOnce } from "@/lib/chunkReload";
import { reportError } from "@/lib/errorReporting";

/**
 * ErrorBoundary — catches render-time errors anywhere in its child tree
 * and shows a recovery UI instead of leaving the user staring at a
 * blank page. Wraps the route tree in App.tsx so transitioning to any
 * lazy chunk that throws during render (engine mis-wire, missing
 * import, chunk-load failure, etc.) shows a "something went wrong"
 * card with a hard-reload + back-to-map option rather than crashing
 * the whole app.
 *
 * Intentionally NOT auto-recovering: a render error means the chunk's
 * state is inconsistent and re-rendering it usually loops. The "Back
 * to map" hard-navigates so React's tree fully unmounts.
 */
interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  /** Optional label to identify the boundary in error reports
   *  (e.g. "route", "chronicle-layout"). Logged via console.error. */
  label?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A stale-deploy chunk-load failure is recoverable, unlike a render
    // exception: one fresh reload refetches index.html and the new hashes.
    // Try that first (guarded against loops); if it fires, the page is
    // navigating away so there's nothing more to do.
    if (isChunkLoadError(error) && attemptChunkReloadOnce()) return;
    // Anonymous, scrubbed report (no-op when analytics is off / opted out),
    // plus the full error in dev tools.
    reportError("boundary", error, this.props.label);
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? ":" + this.props.label : ""}]`, error, info.componentStack);
  }

  handleBackToMap = () => {
    try {
      sessionStorage.setItem("hasEnteredMap", "1");
    } catch {
      // private-browsing edge case — fall through to navigation
    }
    // Hard-navigate so React fully unmounts the broken tree.
    window.location.href = "/";
  };

  handleHardReload = () => {
    // Cache-bust query string forces the browser to refetch the
    // document rather than serve a stale copy from its HTTP cache.
    window.location.href = window.location.pathname + "?v=" + Date.now();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 grain-overlay">
        <div
          className="max-w-md w-full rounded-sm px-6 py-7 text-center"
          style={{ background: "var(--surface-parchment)", border: "1px solid hsl(30, 12%, 22%)" }}
        >
          <h2 className="font-display text-base text-foreground uppercase tracking-[0.2em] mb-3 gold-text-glow">
            Something Went Wrong
          </h2>
          <p className="font-body text-sm text-muted-foreground leading-relaxed mb-5">
            The chronicle hit an unexpected error while rendering this
            screen. Your saved progress is intact. You can return to
            the map and pick up where you left off.
          </p>
          {this.state.error?.message && (
            <p
              className="font-mono text-[10px] text-muted-foreground/60 mb-5 italic break-words text-left bg-background/40 rounded-sm px-3 py-2"
              style={{ border: "1px solid hsl(30, 10%, 16%)" }}
            >
              {this.state.error.message.slice(0, 240)}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={this.handleBackToMap}
              className="w-full min-h-[44px] rounded-sm font-display text-[10px] uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 gold-glow transition-colors px-3"
            >
              Back to the Map
            </button>
            <button
              onClick={this.handleHardReload}
              className="w-full min-h-[40px] rounded-sm font-display text-[10px] uppercase tracking-[0.15em] text-muted-foreground border border-border/40 hover:border-border/70 hover:text-foreground transition-colors px-3"
            >
              Reload this Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
